import {
    FlexBox,
    AppTable,
    FlexRow,
} from 'components'
import React, { useMemo, useState, useRef, useEffect } from 'react'
import { useTabletAndBelowMediaQuery } from 'layout'
import { Card, Button, Modal } from 'semantic-ui-react'
import { AppPrimaryButton, AppButton } from 'elements'
import { DotsThreeVertical, PencilSimple, Trash, Envelope, Info } from 'phosphor-react'
import { AuthService } from 'utility/services/auth'
import { canManageDepartmentsByRole } from 'utility/role-model'
import styled from 'styled-components'

const ActionDropdownContainer = styled.div`
    position: relative;
    display: inline-block;
`

const DropdownMenu = styled.div`
    position: absolute;
    top: 100%;
    right: 0;
    background: white;
    border: 1px solid #e8e9e9;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    min-width: 200px;
    z-index: 1000;
    margin-top: 8px;
    padding: 4px 0;
`

const MenuItem = styled.div`
    padding: 12px 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 12px;
    transition: background-color 0.2s ease;
    font-size: 14px;
    color: ${props => props.danger ? '#dc3545' : '#333'};

    &:hover {
        background-color: #f8f9fa;
    }
`

const ActionBoxes = ({ onEdit, onRemove, onSetAutoEmail, onAddAdditionalInfo, onEditAdditionalInfo, item }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const dropdownRef = useRef(null)
    // role model (opted-in companies): only Admins change departments - the server refuses the others
    const canManage = canManageDepartmentsByRole(AuthService.getUser())

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    const handleEdit = () => {
        onEdit(item)
        setDropdownOpen(false)
    }

    const handleSetNotificationEmail = () => {
        onSetAutoEmail(item)
        setDropdownOpen(false)
    }

    const handleAdditionalInfo = () => {
        if (item.IsCustomerAdditionalInfoAvailable) {
            onEditAdditionalInfo(item)
        } else {
            onAddAdditionalInfo(item)
        }
        setDropdownOpen(false)
    }

    const handleDeleteClick = () => {
        setDeleteConfirmOpen(true)
        setDropdownOpen(false)
    }

    const handleDeleteConfirm = () => {
        onRemove(item.unique_id)
        setDeleteConfirmOpen(false)
    }

    return (
        <>
            <ActionDropdownContainer ref={dropdownRef}>
                <Button
                    icon
                    basic
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    style={{ padding: '8px', minWidth: 'auto' }}
                >
                    <DotsThreeVertical size={20} />
                </Button>
                {dropdownOpen && (
                    <DropdownMenu>
                        {canManage && (
                            <MenuItem onClick={handleEdit}>
                                <PencilSimple size={16} />
                                <span>Edit</span>
                            </MenuItem>
                        )}
                        <MenuItem onClick={handleSetNotificationEmail}>
                            <Envelope size={16} />
                            <span>Set Notification Email</span>
                        </MenuItem>
                        {canManage && (
                            <MenuItem onClick={handleAdditionalInfo}>
                                <Info size={16} />
                                <span>{item.IsCustomerAdditionalInfoAvailable ? 'Edit Additional Info' : 'Add Additional Info'}</span>
                            </MenuItem>
                        )}
                        {canManage && (
                            <MenuItem onClick={handleDeleteClick} danger>
                                <Trash size={16} />
                                <span>Delete</span>
                            </MenuItem>
                        )}
                    </DropdownMenu>
                )}
            </ActionDropdownContainer>
            <Modal
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                size="tiny"
                className="model"
            >
                <Modal.Header>Confirm Delete</Modal.Header>
                <Modal.Content>
                    <p>Are you sure you want to delete this customer?</p>
                </Modal.Content>
                <Modal.Actions>
                    <AppButton onClick={() => setDeleteConfirmOpen(false)}>
                        Cancel
                    </AppButton>
                    <AppPrimaryButton onClick={handleDeleteConfirm} style={{ backgroundColor: '#dc3545' }}>
                        Delete
                    </AppPrimaryButton>
                </Modal.Actions>
            </Modal>
        </>
    )
}

const CustomerCard = ({
    item,
    remove,
    onEdit,
    onEditAdditionalInfo,
    onAddAdditionalInfo,
    onSetAutoEmail,
}) => (
    <Card fluid>
        <Card.Content>
            <Card.Header>{item.customer_name}</Card.Header>
            <Card.Description>
                <FlexRow>
                    {' '}
                    <Card.Meta>Group:</Card.Meta>&nbsp; {item.CustomerGroupName}
                </FlexRow>
                <FlexRow>
                    {' '}
                    <Card.Meta>Email:</Card.Meta>&nbsp; {item.Email}
                </FlexRow>
                <FlexRow>
                    {' '}
                    <Card.Meta>Phone:</Card.Meta>&nbsp; {item.Phone}
                </FlexRow>
                <FlexRow>
                    {' '}
                    <Card.Meta>Address:</Card.Meta>&nbsp; {item.Address}{' '}
                    {item.City}, {item.State} {item.Zip}
                </FlexRow>
                <FlexBox className="justify-content-end mt-2">
                    <ActionBoxes
                        item={item}
                        onEdit={() => onEdit(item)}
                        onRemove={(id) => remove(id)}
                        onSetAutoEmail={() => onSetAutoEmail(item)}
                        onAddAdditionalInfo={onAddAdditionalInfo}
                        onEditAdditionalInfo={onEditAdditionalInfo}
                    />
                </FlexBox>
            </Card.Description>
        </Card.Content>
    </Card>
)

export const CustomerList = ({
    data,
    filter,
    pageChanged,
    totalCount,
    remove,
    onEdit,
    onEditAdditionalInfo,
    onAddAdditionalInfo,
    onSetAutoEmail,
}) => {
    const isMobile = useTabletAndBelowMediaQuery()
    const columns = useMemo(
        () => [
            {
                title: 'Customer Name',
                key: 'customer_name',
            },
            {
                title: 'Customer Group',
                key: 'CustomerGroupName',
            },
            {
                title: 'Email',
                key: 'Email',
            },
            {
                title: 'Phone',
                key: 'Phone',
            },
            {
                title: 'Address',
                key: 'Address,',
                render: item =>
                    isMobile ? (
                        item.Address
                    ) : (
                        <div style={{ minHeight: 60, textAlign: 'left' }}>
                            <div><b>Address:</b> {item.Address}</div>
                            <div><b>City:</b> {item.City}</div>
                            <div><b>State:</b> {item.State}</div>
                            <div><b>Zip:</b> {item.Zip}</div>
                        </div>
                    ),
            },
            // Only show these columns on mobile
            ...(
                isMobile
                    ? [
                        { title: 'City', key: 'City' },
                        { title: 'State', key: 'State' },
                        { title: 'Zip', key: 'Zip' },
                      ]
                    : []
            ),
            {
                title: 'Actions',
                key: 'Actions',
                render: item => (
                    <FlexBox className="justify-content-center">
                        <ActionBoxes
                            item={item}
                            onEdit={() => onEdit(item)}
                            onRemove={(id) => remove(id)}
                            onSetAutoEmail={() => onSetAutoEmail(item)}
                            onAddAdditionalInfo={onAddAdditionalInfo}
                            onEditAdditionalInfo={onEditAdditionalInfo}
                        />
                    </FlexBox>
                ),
            },
        ],
        [data, totalCount, filter, isMobile],
    )
    return (
        <>
            <AppTable
                data={data.filter(customer => customer.unique_id !== 23)}
                columns={columns}
                pageChanged={pageChanged}
                filter={filter}
                totalCount={totalCount}
                mobileCard={
                    <CustomerCard
                        remove={remove}
                        onEdit={onEdit}
                        onEditAdditionalInfo={onEditAdditionalInfo}
                        onAddAdditionalInfo={onAddAdditionalInfo}
                        onSetAutoEmail={onSetAutoEmail}
                    />
                }
            />
        </>
    )
}
